/**
 * astro-routes — маршруты AstroShell (новая витрина астрослужбы, спека
 * 2026-08-06-svaha-rebuild-design.md).
 *
 * Карта экранов = карта спеки, ничего сверх:
 *   /astro                 — ДОМ (таб «Люди»: ты + пары + круг + одна кнопка)
 *   /astro/today           — СЕГОДНЯ (таб: твой день + пульсы пар)
 *   /astro/me              — ты сам (своя карта + свои разборы)
 *   /astro/person/:id      — человек круга (карта + маркетплейс разборов)
 *   /astro/person/:id/edit — правка карты человека
 *   /astro/pair/:partnerId — пара (шоу → сцена → вердикт)
 *   /astro/new             — проверить нового человека (ссылка / дата руками)
 *   /astro/stars           — шестерёнка: баланс ✦ (не таб)
 *
 * Навигация — стек в памяти (back = pop), URL держится replaceState'ом только
 * для refresh/deep-link (как в StudiosShell: история не источник «назад»).
 */

export type AstroRoute =
  | { view: 'home' }
  | { view: 'today' }
  | { view: 'me' }
  | { view: 'person'; id: string }
  | { view: 'edit'; id: string }
  | { view: 'pair'; partnerId: number }
  | { view: 'new' }
  | { view: 'stars' };

export const ASTRO_BASE = '/astro';

export function parseAstroRoute(pathname: string): AstroRoute {
  const mEdit = pathname.match(/^\/astro\/person\/([^/]+)\/edit\/?$/);
  if (mEdit) return { view: 'edit', id: decodeURIComponent(mEdit[1]!) };
  const mPerson = pathname.match(/^\/astro\/person\/([^/]+)\/?$/);
  if (mPerson) return { view: 'person', id: decodeURIComponent(mPerson[1]!) };
  const mPair = pathname.match(/^\/astro\/pair\/([^/]+)\/?$/);
  if (mPair) {
    // Мусор вместо числа → дом (П5: любой вход имеет осмысленный путь).
    const partnerId = Number(mPair[1]);
    return Number.isFinite(partnerId) && partnerId !== 0
      ? { view: 'pair', partnerId }
      : { view: 'home' };
  }
  if (/^\/astro\/today\/?$/.test(pathname)) return { view: 'today' };
  if (/^\/astro\/me\/?$/.test(pathname)) return { view: 'me' };
  if (/^\/astro\/new\/?$/.test(pathname)) return { view: 'new' };
  if (/^\/astro\/stars\/?$/.test(pathname)) return { view: 'stars' };
  return { view: 'home' };
}

export function astroRoutePath(route: AstroRoute): string {
  switch (route.view) {
    case 'today':
      return `${ASTRO_BASE}/today`;
    case 'me':
      return `${ASTRO_BASE}/me`;
    case 'person':
      return `${ASTRO_BASE}/person/${encodeURIComponent(route.id)}`;
    case 'edit':
      return `${ASTRO_BASE}/person/${encodeURIComponent(route.id)}/edit`;
    case 'pair':
      return `${ASTRO_BASE}/pair/${route.partnerId}`;
    case 'new':
      return `${ASTRO_BASE}/new`;
    case 'stars':
      return `${ASTRO_BASE}/stars`;
    default:
      return ASTRO_BASE;
  }
}

/** Таб, которому принадлежит корень стека. Всё, кроме «Сегодня», живёт под «Люди». */
export type AstroTab = 'people' | 'today';

export function tabRoot(tab: AstroTab): AstroRoute {
  return tab === 'today' ? { view: 'today' } : { view: 'home' };
}

/** Начальный стек из URL (deep-link/refresh): глубокий экран получает под собой
 *  свой таб-корень, чтобы back вёл к дому, а не закрывал Mini App. */
export function initialAstroStack(route: AstroRoute): AstroRoute[] {
  if (route.view === 'home' || route.view === 'today') return [route];
  return [{ view: 'home' }, route];
}

/**
 * Легаси-адрес старой астростудии → новый /astro (подмена входа, приказ ректора
 * 6 авг: «чтобы у всех людей было так»). УЖЕ ОТПРАВЛЕННЫЕ ссылки и web_app-кнопки
 * в чатах указывают на /astrology-studio/... — редирект в App.tsx через этот
 * маппер держит их живыми НАВСЕГДА (контракт, не времянка). Query (?invite/
 * ?compat/?pair/?chat) едет как есть — новый шелл её понимает.
 * Неизвестный легаси-путь → дом (П5).
 */
export function legacyAstrologyPathToAstro(pathname: string, search: string): string {
  const seg = pathname
    .replace(/^\/astrology-studio\/?/, '')
    .split('/')
    .filter(Boolean);
  let path: string = ASTRO_BASE;
  if (seg[0] === 'today') path = `${ASTRO_BASE}/today`;
  else if (seg[0] === 'me') path = `${ASTRO_BASE}/me`;
  else if (seg[0] === 'stars') path = `${ASTRO_BASE}/stars`;
  else if (seg[0] === 'cycle') path = `${ASTRO_BASE}/new`;
  else if (seg[0] === 'person' && seg[1]) {
    path =
      seg[2] === 'edit' ? `${ASTRO_BASE}/person/${seg[1]}/edit` : `${ASTRO_BASE}/person/${seg[1]}`;
  } else if (seg[0] === 'compare' && seg[1]) path = `${ASTRO_BASE}/pair/${seg[1]}`;
  // mycircle / pairs / прочее → дом (там же живут люди и пары)
  return path + search;
}
